import { markets } from "@/config/markets";
import { cn } from "@/lib/utils";
import type { LocationOption } from "@/types/location-selector";
import { getAreaCountry, type MapAreaId } from "@/utils/location";
import { CheckIcon } from "lucide-react";
import Image from "next/image";
import { CommandItem } from "../ui/command";

export function SelectedAreaItem({
  area,
  options,
}: {
  area: MapAreaId;
  options: LocationOption[];
}) {
  const selectedOption = options.find((opt) => opt.value === area);
  const { accentClass } = markets[getAreaCountry(area)];

  if (!selectedOption) return null;

  return (
    <CommandItem className="flex items-center gap-3 px-2 py-3 bg-transparent data-[selected=true]:bg-transparent">
      <div className="relative shrink-0">
        <Image
          src={`/search/search-${area}.png`}
          alt={selectedOption.label}
          width={40}
          height={40}
          className={cn(accentClass, "rounded-sm")}
          sizes="40px"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      </div>
      <div className="flex flex-col gap-0.5 flex-1 min-w-0">
        <span className="font-medium text-base leading-6 truncate">
          {selectedOption.label}
        </span>
        <span className="text-sm text-muted-foreground leading-5 truncate">
          {selectedOption.description}
        </span>
      </div>
      <CheckIcon className="size-5 text-foreground shrink-0" />
    </CommandItem>
  );
}

export function LocationOptionItem({
  option,
  onSelect,
}: {
  option: LocationOption;
  onSelect: () => void;
}) {
  const { accentClass } = markets[getAreaCountry(option.value)];

  return (
    <CommandItem
      value={option.value}
      onSelect={onSelect}
      className="flex items-center gap-3 cursor-pointer px-2 py-3"
    >
      <div className="relative shrink-0">
        <Image
          src={`/search/search-${option.value}.png`}
          alt={option.label}
          width={40}
          height={40}
          className={cn(accentClass, "rounded-sm")}
          sizes="40px"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      </div>
      <div className="flex flex-col gap-0.5 flex-1 min-w-0">
        <span className="font-medium text-base leading-6 truncate">
          {option.label}
        </span>
        <span className="text-sm text-muted-foreground leading-5 truncate">
          {option.description}
        </span>
      </div>
    </CommandItem>
  );
}
